import { useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Monitor, Network, Server, Wifi, Settings, Wrench, Activity, ShieldCheck, Building2, Clock, Send, Loader2 } from "lucide-react";

const SERVICES = [
  { slug: "computer-services", icon: Monitor, title: "Computer Services", desc: "Setup, optimisation and maintenance for Windows, Mac and Linux computers." },
  { slug: "it-services", icon: Server, title: "IT Services", desc: "Managed IT, software deployment and infrastructure administration." },
  { slug: "network-services", icon: Network, title: "Network Services", desc: "Design, deployment and management of enterprise networks." },
  { slug: "network-setup", icon: Wifi, title: "Network Setup", desc: "Router, switch, firewall and Wi-Fi installation and provisioning." },
  { slug: "network-configuration", icon: Settings, title: "Network Configuration", desc: "VLANs, routing, VPN, QoS and secure configuration." },
  { slug: "network-troubleshooting", icon: Wrench, title: "Network Troubleshooting", desc: "Fast diagnosis and resolution of connectivity and performance issues." },
  { slug: "network-monitoring", icon: Activity, title: "Network Monitoring", desc: "24/7 proactive monitoring, alerting and health reporting." },
  { slug: "network-security", icon: ShieldCheck, title: "Network Security", desc: "Firewalls, zero-trust, hardening and vulnerability remediation." },
  { slug: "computer-troubleshooting", icon: Wrench, title: "Computer Troubleshooting", desc: "Hardware and software diagnostics and repair." },
  { slug: "business-it-support", icon: Building2, title: "Business IT Support", desc: "Reliable IT support tailored for businesses of every size." },
  { slug: "24-7-it-support", icon: Clock, title: "24/7 IT Support", desc: "Round-the-clock technical support whenever you need it." },
];

export default function Services() {
  const { slug } = useParams();
  const selected = SERVICES.find((s) => s.slug === slug);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: selected?.title || SERVICES[0].title, message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.email || !form.message) { toast.error("Please fill name, email and message."); return; }
    setLoading(true);
    try {
      await api.post("/service-inquiry", form);
      toast.success("Your inquiry was sent. Our team will contact you shortly.");
      setForm({ ...form, name: "", email: "", phone: "", message: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="mb-12">
        <span className="text-xs font-mono uppercase tracking-widest text-cyan-400">IT &amp; Network Services</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-2">
          {selected ? selected.title : "Professional Technology Services"}
        </h1>
        <p className="text-slate-400 mt-3 max-w-2xl">
          {selected ? selected.desc : "Computer, IT and networking services delivered by our Madeira specialists — remote and on-site. No account required to request a service."}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
          {SERVICES.map((s, i) => (
            <button key={s.slug} onClick={() => setForm({ ...form, service: s.title })}
              data-testid={`service-${s.slug}`}
              className={`tactical-card p-5 text-left fade-up ${form.service === s.title ? "border-[#2563EB]" : ""}`}
              style={{ animationDelay: `${i * 40}ms` }}>
              <s.icon className="w-7 h-7 text-cyan-400 mb-3" />
              <h3 className="font-display font-semibold text-white text-base">{s.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{s.desc}</p>
            </button>
          ))}
        </div>

        <div>
          <div className="tactical-card p-6 sticky top-24">
            <h2 className="font-display font-semibold text-white mb-1">Request this service</h2>
            <p className="text-xs text-slate-400 mb-5">Selected: <span className="text-cyan-400">{form.service}</span></p>
            <div className="space-y-4">
              <Input label="Name *" k="name" form={form} onChange={set} testid="inquiry-name-input" />
              <Input label="Email *" k="email" type="email" form={form} onChange={set} testid="inquiry-email-input" />
              <Input label="Phone" k="phone" form={form} onChange={set} testid="inquiry-phone-input" />
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Service</label>
                <select value={form.service} onChange={set("service")} data-testid="inquiry-service-select"
                  className="w-full bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#2563EB]">
                  {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Message *</label>
                <textarea value={form.message} onChange={set("message")} rows={4} data-testid="inquiry-message-input"
                  className="w-full bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#2563EB]" />
              </div>
              <button onClick={submit} disabled={loading} data-testid="inquiry-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Send Inquiry
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, k, type = "text", form, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
      <input value={form[k]} onChange={onChange(k)} type={type} data-testid={testid}
        className="w-full bg-[#0A0E1A] border border-[#1E2F4D] rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#2563EB]" />
    </div>
  );
}
